"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { Scale, Shield, FileText, Printer, Download, ChevronRight } from "lucide-react";
const termsData = [{
  id: "confidentiality",
  title: "1. Service Confidentiality",
  content: "We maintain strict confidentiality regarding all audit findings, proprietary systems, and business operations discovered during our engagement. All personnel are bound by comprehensive non-disclosure agreements. Client data, including but not limited to source code, infrastructure details, and vulnerability reports, is never shared with third parties without explicit written consent, except as strictly required by applicable law."
}, {
  id: "audit-scope",
  title: "2. Audit Scope and Limitations",
  content: "The scope of our audit is strictly limited to the systems, applications, and processes explicitly defined in the mutually agreed Statement of Work (SOW). We do not guarantee the discovery of all vulnerabilities, as security is a continuously evolving landscape. Our assessments represent a point-in-time evaluation and should not be construed as a permanent guarantee of system security or immunity from future breaches."
}, {
  id: "compliance",
  title: "3. Compliance Standards",
  content: "Our auditing processes and methodologies adhere to industry-recognized standards, including but not limited to ISO/IEC 27001, SOC 2, and relevant local data protection regulations (such as GDPR or CCPA). While our reports may assist in your compliance efforts, the final responsibility for achieving and maintaining regulatory compliance rests solely with the client organization."
}, {
  id: "security-protocols",
  title: "4. Security Protocols",
  content: "We employ rigorous internal security protocols to protect client data during and after the engagement. This includes end-to-end encryption for data in transit and at rest, multi-factor authentication for all internal systems, and secure data destruction procedures upon project completion or client request. We maintain our own continuous security monitoring to ensure the integrity of our auditing infrastructure."
}, {
  id: "liability",
  title: "5. Liability Limitations",
  content: "To the maximum extent permitted by law, our total aggregate liability arising out of or in connection with the services provided shall be limited to the total fees paid by the client for the specific audit service giving rise to the claim. We shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including loss of profits, data, or business continuity, regardless of the theory of liability."
}, {
  id: "dispute-resolution",
  title: "6. Dispute Resolution",
  content: "Any disputes, controversies, or claims arising out of or relating to these Terms of Service, or the breach thereof, shall be resolved through good-faith negotiations. If a resolution cannot be reached within thirty (30) days, the dispute shall be submitted to binding arbitration in accordance with the rules of the designated arbitration association in our primary jurisdiction of operation."
}];
export default function TermsOfService() {
  return <section id="terms-of-service" className="bg-background text-foreground py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        
        {}
        <div className="flex flex-col items-center text-center mb-12 md:mb-16 space-y-4">
          <Badge variant="outline" className="px-3 py-1 text-sm border-primary/30 text-primary bg-primary/5">
            <Scale className="w-4 h-4 mr-2" />
            Legal Documentation
          </Badge>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default tracking-tight">
            Terms of Service
          </h1>
          <p className="text-muted-foreground max-w-2xl text-lg">
            Please read these terms carefully before engaging with our auditing and compliance services. These terms govern the scope, confidentiality, and limitations of our professional relationship.
          </p>
          <div className="flex items-center gap-4 pt-4 text-sm text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <FileText className="w-4 h-4" />
              Version 2.4
            </span>
            <span className="w-1 h-1 rounded-lg bg-border"></span>
            <span>Last Updated: October 15, 2023</span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-8 lg:gap-12 items-start">
          
          {}
          <aside className="hidden lg:block sticky top-24 space-y-6">
            <Card className="border-border/50 shadow-sm">
              <CardHeader className="pb-4">
                <CardTitle className="text-lg font-default flex items-center gap-2">
                  <Shield className="w-5 h-5 text-primary" />
                  Contents
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <nav className="flex flex-col">
                  {termsData.map((item, i) => <Link key={item.id} data-index={i} to={`#${item.id}`} className="px-6 py-3 text-sm text-muted-foreground hover:text-foreground hover:bg-accent/50 transition-colors border-l-2 border-transparent hover:border-primary flex items-center justify-between group">
                      <span className="truncate pr-4">{item.title}</span>
                      <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </Link>)}
                </nav>
              </CardContent>
            </Card>

            <div className="flex flex-col gap-3">
              <Button variant="outline" className="w-full justify-start">
                <Printer className="w-4 h-4 mr-2" />
                Print Document
              </Button>
              <Button variant="outline" className="w-full justify-start">
                <Download className="w-4 h-4 mr-2" />
                Download PDF
              </Button>
            </div>
          </aside>

          {}
          <Card className="border-border/50 shadow-md">
            <CardContent className="p-6 md:p-10 space-y-10">
              
              <div className="space-y-4 text-muted-foreground">
                <p className="text-base leading-relaxed">
                  These Terms of Service ("Terms") constitute a legally binding agreement made between you, whether personally or on behalf of an entity ("Client", "you"), and our organization ("Company", "we", "us", or "our"), concerning your access to and use of our auditing, compliance, and security assessment services.
                </p>
                <p className="text-base leading-relaxed">
                  By engaging our services, you agree that you have read, understood, and agree to be bound by all of these Terms. If you do not agree with all of these Terms, then you are expressly prohibited from using our services and you must discontinue use immediately.
                </p>
              </div>

              <Separator className="bg-border/60" />

              <div className="space-y-12">
                {termsData.map((section, i) => <div key={section.id} data-index={i} id={section.id} className="scroll-mt-24 space-y-4">
                    <h2 className="text-xl md:text-2xl font-semibold font-default text-foreground">
                      {section.title}
                    </h2>
                    <p className="text-muted-foreground leading-relaxed">
                      {section.content}
                    </p>
                  </div>)}
              </div>

              <Separator className="bg-border/60" />

              <div className="bg-muted/30 rounded-lg p-6 border border-border/50">
                <h3 className="text-lg font-semibold font-default mb-2">Questions or Concerns?</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  If you have any questions regarding these Terms of Service or require clarification on any specific clause before proceeding with an engagement, please contact our legal department.
                </p>
                <Button asChild variant="secondary">
                  <Link to="/ContactPage">Contact Legal Team</Link>
                </Button>
              </div>

            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}